import React from "react";
import { Star, MapPin, Briefcase, BadgeCheck, Languages, Zap, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import PriceTag, { lawyerPricing } from "@user/components/PriceTag";

export const LawyerCard = ({ lawyer, onBook, onInstant }) => {
  const initials = lawyer.name.split(" ").slice(-2).map((x) => x[0]).join("");
  const available = !!lawyer.available;
  const isPulsing = !!lawyer.pulse_until && new Date(lawyer.pulse_until).getTime() > Date.now();
  return (
    <div
      data-testid={`lawyer-card-${lawyer.id}`}
      className={`group bg-white border rounded-2xl p-5 hover:shadow-lg hover:-translate-y-1 transition-all duration-200 relative ${
        isPulsing ? "border-emerald-300 ring-1 ring-emerald-200" : "border-slate-200"
      }`}
    >
      {isPulsing && (
        <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider" data-testid={`pulse-badge-${lawyer.id}`}>
          <span className="relative inline-flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75 animate-ping" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-600" />
          </span>
          Live now
        </div>
      )}
      {!isPulsing && available && (
        <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-emerald-50 text-emerald-700 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider" data-testid={`available-badge-${lawyer.id}`}>
          <span className="adv-pulse-dot" /> Available now
        </div>
      )}
      <div className="flex gap-4">
        {lawyer.photo ? (
          <img src={lawyer.photo} alt={lawyer.name} className="h-16 w-16 rounded-2xl object-cover border border-slate-200" />
        ) : (
          <div className="h-16 w-16 rounded-2xl adv-navy grid place-items-center font-bold text-lg shrink-0 text-[#D4AF37]">
            {initials}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <h3 className="font-bold text-[#0A2540] text-base leading-tight truncate" style={{ fontFamily: "Poppins" }}>{lawyer.name}</h3>
                {lawyer.verified && <BadgeCheck size={14} className="text-[#D4AF37] shrink-0" data-testid={`verified-${lawyer.id}`} />}
              </div>
              <p className="text-xs text-slate-500 mt-0.5">{lawyer.specialization}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-2 text-xs text-slate-600 flex-wrap">
            <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-800 px-2 py-0.5 rounded-md font-bold">
              <Star size={11} fill="currentColor" />{lawyer.rating.toFixed(1)}
            </span>
            <span className="inline-flex items-center gap-1"><MapPin size={12} />{lawyer.city}</span>
            <span className="inline-flex items-center gap-1"><Briefcase size={12} />{lawyer.experience}y</span>
          </div>
          {lawyer.languages?.length > 0 && (
            <div className="mt-2 flex items-center gap-1 text-[11px] text-slate-500 truncate">
              <Languages size={12} /> {lawyer.languages.slice(0, 3).join(" · ")}
            </div>
          )}
        </div>
      </div>
      <p className="mt-3 text-sm text-slate-600 line-clamp-2">{lawyer.bio}</p>
      <div className="mt-4 flex items-end justify-between gap-2 flex-wrap">
        <div>
          <span className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Chat from</span>
          <PriceTag pricing={lawyerPricing(lawyer, "chat")} layout="card" size="md" testid={`price-${lawyer.id}`} />
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to={`/lawyers/${lawyer.id}`}>
            <Button variant="outline" size="sm" data-testid={`view-profile-${lawyer.id}`} className="rounded-full border-slate-200">
              Profile
            </Button>
          </Link>
          {available ? (
            <>
              <Button
                onClick={() => onInstant?.(lawyer)}
                size="sm"
                data-testid={`instant-btn-${lawyer.id}`}
                className="rounded-full adv-btn-gold font-bold"
                title="Start Instant Consultation"
              >
                <Zap size={13} fill="currentColor" /> Instant
              </Button>
              <Button
                onClick={() => onBook?.(lawyer)}
                size="sm"
                variant="outline"
                data-testid={`book-btn-${lawyer.id}`}
                className="rounded-full border-[#0A2540]/20 hover:bg-[#0A2540] hover:text-white font-semibold"
                title="Book for Later"
              >
                <Calendar size={13} /> Later
              </Button>
            </>
          ) : (
            <Button
              onClick={() => onBook?.(lawyer)}
              size="sm"
              data-testid={`book-btn-${lawyer.id}`}
              className="rounded-full adv-btn-gold font-bold"
              title="Book a slot"
            >
              <Calendar size={13} /> Book slot
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export const LawyerCardSkeleton = () => (
  <div className="bg-white border border-slate-200 rounded-2xl p-5">
    <div className="flex gap-4">
      <div className="h-16 w-16 rounded-2xl adv-skeleton" />
      <div className="flex-1 space-y-2">
        <div className="h-4 rounded adv-skeleton w-2/3" />
        <div className="h-3 rounded adv-skeleton w-1/2" />
        <div className="h-3 rounded adv-skeleton w-1/3" />
      </div>
    </div>
    <div className="mt-4 space-y-2">
      <div className="h-3 rounded adv-skeleton w-full" />
      <div className="h-3 rounded adv-skeleton w-4/5" />
    </div>
  </div>
);

export default LawyerCard;
